import {useState, useCallback, Dispatch, SetStateAction} from 'react';
import {useTranslation} from 'react-i18next';
import * as Sentry from '@sentry/react';

import {useAppDispatch, showToastAction} from 'store';
import {RoomsService, ReportableRoomType} from 'services';

export interface ReportRoomParticipantTarget {
    user_id: number;
    first_name: string;
    last_name: string;
    room_id: number;
    room_type: ReportableRoomType;
}

export const useReportRoomParticipantLogic = ({
    setOpen,
    target,
}: {
    setOpen: Dispatch<SetStateAction<boolean>>;
    target: ReportRoomParticipantTarget | undefined;
}) => {
    const [reportBody, setReportBody] = useState<{
        info: string;
        subject: string;
    }>({info: '', subject: ''});
    const [submitting, setSubmitting] = useState(false);
    const {t} = useTranslation();
    const dispatch = useAppDispatch();

    const submitReport = useCallback(async () => {
        if (!target || submitting) return;

        setSubmitting(true);
        try {
            await RoomsService.reportRoomParticipant({
                roomType: target.room_type,
                roomId: target.room_id,
                reportedUser: target.user_id,
                subject: reportBody.subject,
                info: reportBody.info,
            });
            setOpen(false);
            dispatch(
                showToastAction({
                    open: true,
                    vertical: 'top',
                    horizontal: 'center',
                    title: t('report.reportSubmitted'),
                    subTitle: t('report.weReceivedYourReport'),
                }),
            );
        } catch (error) {
            Sentry.captureException(error);
            dispatch(
                showToastAction({
                    open: true,
                    vertical: 'top',
                    horizontal: 'center',
                    title: t('common.anErrorHasOccurred'),
                    subTitle: t('common.pleaseTryAgain'),
                    icon: 'error',
                }),
            );
        } finally {
            setSubmitting(false);
        }
    }, [dispatch, reportBody, setOpen, submitting, t, target]);

    return {
        reportBody,
        setReportBody,
        submitReport,
        submitting,
        t,
    };
};
